import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Trips } from "@screens/trips-created/types/types";
import { TRIP_STATUS } from "utils/index";
import { getTrips } from "services/createTripService";

interface TripState {
  tripCache: Record<string, Trips[] | null>;
  status: Record<string, "idle" | "loading" | "failed">;
}

const initialState: TripState = {
  tripCache: {
    [TRIP_STATUS.IN_REVIEW]: null,
    [TRIP_STATUS.APPROVED]: null,
    [TRIP_STATUS.REJECTED]: null,
    [TRIP_STATUS.RESUBMIT]: null,
    [TRIP_STATUS.ImagesSlot_Pending]: null,
    [TRIP_STATUS.CLOSED]: null,
  },
  status: {},
};

export const fetchTrips = createAsyncThunk(
  "trips/fetchTrips",
  async (data:{tripStatus:string,id:string}, { getState, rejectWithValue }) => {
    const state = getState() as { trips: TripState };

    // If trips for this status already exist, return cached data
    if (state.trips.tripCache[data.tripStatus] !== null) {
      return { status: data.tripStatus, data: state.trips.tripCache[data.tripStatus] };
    }
    try {
      const response = await getTrips(data.tripStatus,data.id);
      return { status: data.tripStatus, data: response ?? [] };
    } catch (error) {
      console.error("Error while fetching trips", error);
      return rejectWithValue(error);
    }
  }
);

const tripSlice = createSlice({
  name: "trips",
  initialState,
  reducers: {
    clearTrips: (state) => {
      Object.keys(state.tripCache).forEach((key) => {
        state.tripCache[key] = null; // Reset each trip status to null instead of deleting
      });
      state.status = {}; // Reset status
    },
    forceRefreshTrips: (state, action: PayloadAction<string>) => {
      state.tripCache[action.payload] = null; // Only refresh the specific trip status
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrips.pending, (state, action) => {
        state.status[action.meta.arg.tripStatus] = "loading";
      })
      .addCase(
        fetchTrips.fulfilled,
        (state, action: PayloadAction<{ status: string; data: Trips[] }>) => {
          state.status[action.payload.status] = "idle";
          state.tripCache[action.payload.status] = action.payload.data;
        }
      )
      .addCase(fetchTrips.rejected, (state, action) => {
        state.status[action.meta.arg.tripStatus] = "failed";
      });
  },
});

export const { clearTrips,forceRefreshTrips } = tripSlice.actions;
export default tripSlice.reducer;
